import { useState, type SubmitEvent } from 'react';
import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import { usePasswordLogin } from '../../api/queryHooks/user';
import { isMfaRequired } from '../../api/types';
import { Loader } from '@f2m3/shared-frontend-ui';

interface PasswordLoginProps {
  email: string;
  onMfaRequired?: (challenge: { mfaToken: string; methods: string[] }) => void;
}

const PasswordLogin = ({ email, onMfaRequired }: PasswordLoginProps) => {
  const { t } = useTranslation();
  const [password, setPassword] = useState('');
  const { mutateAsync: loginWithPassword, isPending } = usePasswordLogin();

  const handleSubmit = async (e: SubmitEvent<HTMLFormElement>) => {
    e.preventDefault();
    try {
      const data = await loginWithPassword({ email, password });
      if (isMfaRequired(data) && onMfaRequired) {
        onMfaRequired({ mfaToken: data.mfa_token, methods: data.methods });
      }
      // Non-MFA success: the newToken callback already stored the session token.
    } catch {
      // Error is surfaced by the usePasswordLogin onError notification.
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      {/* The email was typed on the previous step: password managers look for the username in
          the password's own form, to save and fill the password under it. */}
      <input type="email" className="is-visually-hidden" autoComplete="username" value={email} readOnly tabIndex={-1} aria-hidden="true" />
      <div className="field">
        <label className="label">{t('login.password_label')}</label>
        <div className="control">
          <input className="input" type="password" autoComplete="current-password" value={password} autoFocus onChange={(e) => setPassword(e.target.value)} />
        </div>
      </div>
      {isPending && <Loader />}
      {!isPending && (
        <div className="field is-grouped is-justify-content-space-between">
          <div className="control">
            <Link to="/forgot-password" state={{ email }} className="is-size-7">
              {t('login.forgot_password')}
            </Link>
          </div>
          <div className="control">
            <button className="button is-primary">{t('login.button_login')}</button>
          </div>
        </div>
      )}
    </form>
  );
};

export default PasswordLogin;
